import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  useCreateTaxDocument,
  useDraft,
  useTaxDocument,
  DocumentTypeSelector,
  ReceptorForm,
  LineItemsEditor,
  TotalsPreview,
  ReferenceSelector,
} from "@/features/tax-documents";
import type {
  DteTypeCode,
  ReceptorInput,
  LineItemInput,
  ReferenceInput,
  CreateTaxDocumentInput,
  DraftInput,
  TaxDocumentStatus,
} from "@/features/tax-documents";

export function NuevoDocumentoPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { createDocument, isLoading } = useCreateTaxDocument();
  const { saveDraft, finalizeDraft, isSaving, isFinalizing } = useDraft();

  // Draft ID from URL (if editing an existing draft)
  const draftId = searchParams.get("draftId");
  const { document: existingDraft } = useTaxDocument(draftId ?? undefined);

  // Determine if we're in draft mode
  const isDraft = !!draftId;
  const documentStatus = existingDraft?.status as TaxDocumentStatus | undefined;

  // Form state
  const [documentType, setDocumentType] = useState<DteTypeCode>(33);
  const [issueDate, setIssueDate] = useState<string>(
    new Date().toISOString().split("T")[0]!,
  );
  const [receptor, setReceptor] = useState<ReceptorInput>({
    rut: "",
    razonSocial: "",
    giro: "",
    direccion: "",
    comuna: "",
    ciudad: "",
  });
  const [lineItems, setLineItems] = useState<LineItemInput[]>([
    { nombre: "", cantidad: 1, precioUnitario: 0 },
  ]);
  const [reference, setReference] = useState<ReferenceInput | undefined>(
    undefined,
  );

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  const needsReference = documentType === 61 || documentType === 56;

  // Load existing draft data when editing
  useEffect(() => {
    if (existingDraft?.countryMetadata?.draftInput) {
      const draft = existingDraft.countryMetadata.draftInput as DraftInput;
      setDocumentType(draft.documentType);
      setIssueDate(draft.issueDate);
      if (draft.receptor) {
        setReceptor({
          rut: draft.receptor.rut ?? "",
          razonSocial: draft.receptor.razonSocial ?? "",
          giro: draft.receptor.giro ?? "",
          direccion: draft.receptor.direccion ?? "",
          comuna: draft.receptor.comuna ?? "",
          ciudad: draft.receptor.ciudad ?? "",
        });
      }
      if (draft.lineItems?.length) {
        setLineItems(draft.lineItems);
      }
      if (draft.references?.length) {
        setReference(draft.references[0] as ReferenceInput);
      }
    }
  }, [existingDraft]);

  // Only show draft button for new documents or documents in draft status
  const showDraftButton = !draftId || documentStatus === "draft";

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!receptor.rut) newErrors["receptor.rut"] = "RUT es requerido";
    if (!receptor.razonSocial)
      newErrors["receptor.razonSocial"] = "Razón Social es requerida";
    if (!receptor.giro) newErrors["receptor.giro"] = "Giro es requerido";
    if (!receptor.direccion)
      newErrors["receptor.direccion"] = "Dirección es requerida";
    if (!receptor.comuna) newErrors["receptor.comuna"] = "Comuna es requerida";

    if (lineItems.length === 0) {
      newErrors.lineItems = "Debe agregar al menos un item";
    } else {
      const hasEmptyItem = lineItems.some(
        (item) =>
          !item.nombre || item.cantidad <= 0 || item.precioUnitario <= 0,
      );
      if (hasEmptyItem) {
        newErrors.lineItems =
          "Todos los items deben tener descripción, cantidad y precio";
      }
    }

    if (needsReference) {
      if (!reference?.folioRef || reference.folioRef <= 0) {
        newErrors.reference = "Debe seleccionar el documento de referencia";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      toast.error("Por favor corrige los errores del formulario");
      return;
    }

    const input: CreateTaxDocumentInput = {
      documentType,
      issueDate,
      receptor,
      lineItems: lineItems.filter(
        (item) => item.nombre && item.cantidad > 0 && item.precioUnitario > 0,
      ),
      references: reference ? [reference] : undefined,
    };

    try {
      // If finalizing a draft, use finalizeDraft instead
      if (draftId) {
        const result = await finalizeDraft(draftId);
        toast.success(
          `Documento #${result.document.folio} generado exitosamente`,
        );
        navigate(`/facturacion/emision/${result.document.id}`);
      } else {
        const result = await createDocument(input);
        toast.success(
          `Documento #${result.document.folio} generado exitosamente`,
        );
        navigate(`/facturacion/emision/${result.document.id}`);
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al generar el documento",
      );
    }
  };

  const handleSaveDraft = async () => {
    const draftInput: DraftInput = {
      documentType,
      issueDate,
      receptor,
      lineItems,
      references: reference ? [reference] : undefined,
    };

    try {
      const result = await saveDraft(draftInput, draftId ?? undefined);
      toast.success(
        isDraft ? "Borrador guardado" : "Borrador creado exitosamente",
      );
      // Update URL with draft ID if this is a new draft
      if (!draftId) {
        setSearchParams({ draftId: result.draft.id });
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al guardar borrador",
      );
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/facturacion/emision")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">
            {isDraft ? "Editar Borrador" : "Emitir Documento"}
          </h1>
          <p className="text-muted-foreground">
            {isDraft
              ? "Modifica el borrador o genera el documento final"
              : "Genera un nuevo documento tributario electrónico"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Document Type */}
        <div className="bg-card border rounded-lg p-6">
          <DocumentTypeSelector
            value={documentType}
            onChange={setDocumentType}
          />
        </div>

        {/* Issue Date */}
        <div className="bg-card border rounded-lg p-6">
          <div className="max-w-xs">
            <Label htmlFor="issueDate">Fecha de Emisión</Label>
            <Input
              id="issueDate"
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              className="mt-2"
            />
          </div>
        </div>

        {/* Receptor */}
        <div className="bg-card border rounded-lg p-6">
          <ReceptorForm
            value={receptor}
            onChange={setReceptor}
            errors={
              Object.fromEntries(
                Object.entries(errors)
                  .filter(([k]) => k.startsWith("receptor."))
                  .map(([k, v]) => [k.replace("receptor.", ""), v]),
              ) as Record<keyof ReceptorInput, string>
            }
          />
        </div>

        {/* Line Items */}
        <div className="bg-card border rounded-lg p-6">
          <LineItemsEditor items={lineItems} onChange={setLineItems} />
          {errors.lineItems && (
            <p className="text-sm text-red-500 mt-2">{errors.lineItems}</p>
          )}
        </div>

        {/* Reference (for NC/ND) */}
        {needsReference && (
          <div className="bg-card border rounded-lg p-6">
            <ReferenceSelector
              value={reference}
              onChange={setReference}
              allowedDocTypes={[33, 34, 52]}
            />
            {errors.reference && (
              <p className="text-sm text-red-500 mt-2">{errors.reference}</p>
            )}
          </div>
        )}

        {/* Totals */}
        <TotalsPreview items={lineItems} />

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/facturacion/emision")}
          >
            Cancelar
          </Button>
          {showDraftButton && (
            <Button
              type="button"
              variant="outline"
              onClick={handleSaveDraft}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {isDraft ? "Guardar Cambios" : "Guardar Borrador"}
                </>
              )}
            </Button>
          )}
          <Button type="submit" disabled={isLoading || isFinalizing}>
            {isLoading || isFinalizing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generando...
              </>
            ) : (
              "Generar Documento"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
